import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import SearchBar from '../../components/searchBar/SearchBar';
import Tab from '../../components/tabs/Tab';
import Dropdown from '../../components/inputFields/DropDown';
import Button from '../../components/buttons/Button';
import CustomLoading from '../../components/customLoading/CustomLoading';
import BlogPostCard from '../../components/blog/BlogPostCard';
import Pagination from '../../components/tables/pagination/Pagination';
import { confirmDanger } from '../../utils/confirmDialog';
import { getBlogPosts, deleteBlogPost, patchBlogPost } from '../../services/blogPost';
import { toast } from 'react-toastify';
import { FaPlus } from 'react-icons/fa';

const BlogPosts = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ results: [], count: 0, next: null, previous: null });
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [platformFilter, setPlatformFilter] = useState('rashailagro');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

    const statusOptions = [
        { label: 'All', value: '' },
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
        { label: 'Archived', value: 'archived' }
    ];

    const platformTabs = [
        { id: '', name: 'All Platforms' },
        { id: 'rashailagro', name: 'Rashail Agro' },
        { id: 'rashailtechlab', name: 'Rashail Tech Lab' },
        { id: 'rashailinfotech', name: 'Rashail Infotech' }
    ];

    const fetchPosts = async (query = '') => {
        setLoading(true);
        try {
            const response = await getBlogPosts(query);
            if (response.status === 200) {
                setData(response.data);
            } else {
                toast.error('Failed to fetch posts');
            }
        } catch (error) {
            console.error('Error fetching posts:', error);
            toast.error('Failed to fetch posts');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (query) => {
        setSearchText(query);
        fetchPosts(`?search=${query}&status=${statusFilter}&platfourm=${platformFilter}`);
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        fetchPosts(`?search=${searchText}&status=${status}&platfourm=${platformFilter}`);
    };

    const handlePlatformFilter = (platform) => {
        setPlatformFilter(platform);
        fetchPosts(`?search=${searchText}&status=${statusFilter}&platfourm=${platform}`);
    };

    const handleCreatePost = () => {
        navigate('/blog/posts/create');
    };

    const handleEditPost = (post) => {
        navigate(`/blog/posts/edit/${post.id}`, { state: { post } });
    };

    const handleDeletePost = async (post) => {
        const confirmation = await confirmDanger(
            `Are you sure you want to delete "${post.title}"? This action cannot be undone.`,
            "Delete Post"
        );

        if (confirmation) {
            try {
                const response = await deleteBlogPost(post.id);
                if (response.status === 204) {
                    toast.success('Post deleted successfully');
                    fetchPosts(`?search=${searchText}&status=${statusFilter}&platfourm=${platformFilter}`);
                } else {
                    toast.error('Failed to delete post');
                }
            } catch (error) {
                console.error('Error deleting post:', error);
                toast.error('Failed to delete post');
            }
        }
    };

    const handleToggleStatus = async (post) => {
        const newStatus = post.status === 'published' ? 'draft' : 'published';
        try {
            const response = await patchBlogPost({
                id: post.id,
                data: { status: newStatus }
            });
            if (response.status === 200) {
                toast.success(`Post ${newStatus === 'published' ? 'published' : 'unpublished'} successfully`);
                fetchPosts(`?search=${searchText}&status=${statusFilter}&platfourm=${platformFilter}`);
            } else {
                toast.error('Failed to update post status');
            }
        } catch (error) {
            console.error('Error updating post status:', error);
            toast.error('Failed to update post status');
        }
    };

    const handleViewPost = (post) => {
        window.open(`http://bazaar.rashailagro.in/view/blogs/${post.slug}`, '_blank');
    };


    useEffect(() => {
        fetchPosts(`?platfourm=rashailagro`);
    }, []);

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Blog', link: '/blog/dashboard' },
                { name: 'Posts' },
            ]} />

            <div className="py-3">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                    <StyledHeadingText text="Blog Posts" />
                    <Button
                        onClick={handleCreatePost}
                        className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
                    >
                        <FaPlus size={16} />
                        <span>Add Post</span>
                    </Button>
                </div>

                {/* Platform Tabs */}
                <div className="mb-6">
                    <Tab tabsList={platformTabs} onClick={handlePlatformFilter} activeTab={platformFilter} />
                </div>

                {/* Filters */}
                <div className="flex flex-col lg:flex-row gap-4 mb-6">
                    <div className="flex-1 max-w-md">
                        <SearchBar
                            placeholder="Search posts..."
                            onSearch={handleSearch}
                            value={searchText}
                        />
                    </div>
                    <div className="flex flex-1 items-center space-x-4">
                        <Dropdown
                            options={statusOptions}
                            value={statusFilter}
                            onChange={handleStatusFilter}
                            placeholder="Filter by status"
                            className="w-48"
                        />
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-md ${viewMode === 'grid'
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                </svg>
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-md ${viewMode === 'list'
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Posts Grid/List */}
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <CustomLoading size={100} />
                    </div>
                ) : (
                    <div className={
                        viewMode === 'grid'
                            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                            : "space-y-4"
                    }>
                        {data.results.map((post) => (
                            <BlogPostCard
                                key={post.id}
                                post={post}
                                onEdit={handleEditPost}
                                onDelete={handleDeletePost}
                                onToggleStatus={handleToggleStatus}
                                onView={handleViewPost}
                                viewMode={viewMode}
                            />
                        ))}
                    </div>
                )}

                {!loading && data.results.length === 0 && (
                    <div className="text-center py-12">
                        <div className="text-gray-500 text-lg mb-4">No posts found</div>
                        <Button
                            onClick={handleCreatePost}
                            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary/90 transition-colors"
                        >
                            Create First Post
                        </Button>
                    </div>
                )}

                {/* Pagination */}
                {data.count > 0 && (
                    <div className="mt-8">
                        <Pagination
                            currentPage={currentPage}
                            pageSize={pageSize}
                            totalEntries={data.count}
                            onPageChange={setCurrentPage}
                            data={data}
                            setData={setData}
                        />
                    </div>
                )}
            </div>


        </>
    );
};

export default BlogPosts;
